import type { CalendarPresets } from "@/types";
import type { Mode } from "./calendar-types";

/**
 * The two capability flags that decide whether the extra resource modes exist.
 * Mirrors the calendar page's `LensCaps` (which aliases this type).
 */
export type CalendarModeCaps = {
  doctorDayView: boolean;
  roomsEnabled: boolean;
};

/**
 * The modes a clinic can render at all, from its capability flags.
 *
 * Base day/week/month are always available; `doctors` and `rooms` exist only
 * when their capability flag is on — the mode pills, the views themselves and
 * the backend routes behind them are all gated the same way. The lens layer
 * (calendar/page.tsx `validModesForLens`) narrows this further per role.
 */
export function capabilityModes(caps: CalendarModeCaps): Mode[] {
  return [
    "day",
    "week",
    "month",
    ...(caps.doctorDayView ? (["doctors"] as Mode[]) : []),
    ...(caps.roomsEnabled ? (["rooms"] as Mode[]) : []),
  ];
}

/**
 * The landing mode a role preset asks for.
 *
 * Returns `presets.default_mode` when it names a mode the clinic can actually
 * render (see {@link capabilityModes}); otherwise `null`, meaning "no
 * opinion" — the caller keeps its own fallback.
 *
 * Null-safe on every axis on purpose: presets are optional on the /auth/me
 * payload, `default_mode` is a free-form string there, and a preset that names
 * a capability-gated mode whose flag is OFF must NOT strand the user on a mode
 * with no pill to leave it by.
 */
export function initialModeForPresets(
  presets: CalendarPresets | null | undefined,
  caps: CalendarModeCaps
): Mode | null {
  const requested = presets?.default_mode;
  if (!requested) return null;

  return capabilityModes(caps).includes(requested as Mode)
    ? (requested as Mode)
    : null;
}

/**
 * The three appointment INTENT types (`appointments.type` column). Travel and
 * blockout also appear in preset `default_types` but are separate feeds, not
 * appointment rows — they are deliberately not part of this set.
 */
export const APPOINTMENT_INTENT_TYPES = [
  "clinic_visit",
  "lead_call",
  "internal",
] as const;

export type AppointmentIntentType = (typeof APPOINTMENT_INTENT_TYPES)[number];

/**
 * The appointment intents a role preset narrows a surface to, or `null`
 * meaning "no narrowing" (show every appointment).
 *
 * Reads `presets.default_types` — the same per-role map the calendar's lens
 * path seeds its Show toggles from — and keeps only the appointment intents
 * (see {@link APPOINTMENT_INTENT_TYPES}). Returns `null` when presets are
 * absent, declare no appointment intent at all, or declare all three (no
 * narrowing either way).
 *
 * Consumers today: the coordinator dashboard and the today-schedule timeline,
 * which filter their rows with {@link isVisibleAppointmentType} so a
 * coordinator runs the clinic day without the call-centre's `lead_call` rows.
 *
 * Callers must treat a `null` row type as `clinic_visit` (the calendar page's
 * own mapping rule for legacy rows) — {@link isVisibleAppointmentType} does.
 */
export function presetAppointmentTypes(
  presets: CalendarPresets | null | undefined
): AppointmentIntentType[] | null {
  const declared = presets?.default_types;
  if (!declared || declared.length === 0) return null;

  const intents = APPOINTMENT_INTENT_TYPES.filter((intent) =>
    declared.includes(intent)
  );

  if (intents.length === 0 || intents.length === APPOINTMENT_INTENT_TYPES.length) {
    return null;
  }

  return intents;
}

/**
 * Whether an appointment row passes a preset narrowing from
 * {@link presetAppointmentTypes}. A `null`/unknown row type counts as
 * `clinic_visit` — the same "legacy rows are visits" rule the calendar page
 * applies when mapping API events.
 */
export function isVisibleAppointmentType(
  rowType: string | null | undefined,
  allowed: AppointmentIntentType[] | null
): boolean {
  if (allowed === null) return true;

  const effective = rowType ?? "clinic_visit";

  return allowed.includes(effective as AppointmentIntentType);
}
